import { FC, useMemo, useState } from 'react';
import { Box, Checkbox, IconButton, Modal, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CustomButton from '../../../../../common/CustomButton';
import CustomInput from '../../../../../common/CustomInput';
import { AiIcon } from '../../../../../components/Icons';
import { DeleteIcon } from '../../../../../components/Icons/DeleteIcon';
import {
  PackingListItem,
  useCreateStopPackingListItemMutation,
  useCreateTripPackingListItemMutation,
  useDeleteStopPackingListItemMutation,
  useDeleteTripPackingListItemMutation,
  useGenerateStopPackingListMutation,
  useGenerateTripPackingListMutation,
  useGetStopPackingListQuery,
  useGetTripPackingListQuery,
  useUpdateStopPackingListItemMutation,
  useUpdateTripPackingListItemMutation,
} from '../../../../../store/services/tripsApi';
import AddIcon from '@mui/icons-material/Add';
import { useIsMobile } from '../../../../../hooks/useWindowWidth';

import styles from './TripPacking.module.scss';

type TripPackingListProps = {
  tripId: number;
  stopId?: number;
  variant?: 'default' | 'embedded';
};

const tripEmptyStateLineOne = 'No packing list generated yet.';
const tripEmptyStateLineTwo = 'Click “Generate Packing List” to get a suggested list for your trip.';

const FALLBACK_CATEGORIES = ['Clothing', 'Documents', 'Toiletries', 'Electronics', 'Health', 'Other'];

const toSafeString = (value: unknown) => (typeof value === 'string' ? value : '');

const normalizeItems = (rawItems: unknown): PackingListItem[] => {
  let candidate: unknown = rawItems;
  if (typeof candidate === 'string') {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return [];
    }
  }

  if (Array.isArray(candidate)) {
    return candidate.reduce<PackingListItem[]>((acc, item, index) => {
      if (!item || typeof item !== 'object') return acc;
      const safeItem = item as Record<string, unknown>;
      const parsedId = Number(safeItem.id);
      const parsedSort = Number(safeItem.sort_order);
      const title = toSafeString(safeItem.title);
      if (!title.trim()) return acc;
      acc.push({
        id: Number.isFinite(parsedId) ? parsedId : index + 1,
        category: toSafeString(safeItem.category) || 'Other',
        title,
        is_checked: Boolean(safeItem.is_checked),
        sort_order: Number.isFinite(parsedSort) ? parsedSort : index,
        source: toSafeString(safeItem.source) || 'ai',
        locked_by_user: Boolean(safeItem.locked_by_user),
      });
      return acc;
    }, []);
  }

  if (candidate && typeof candidate === 'object') {
    const entries = Object.entries(candidate as Record<string, unknown>);
    const expanded: PackingListItem[] = [];

    entries.forEach(([category, value], categoryIndex) => {
      if (!Array.isArray(value)) return;
      value.forEach((item, itemIndex) => {
        if (!item) return;
        if (typeof item === 'string') {
          expanded.push({
            id: categoryIndex * 1000 + itemIndex + 1,
            category,
            title: item,
            is_checked: false,
            sort_order: itemIndex,
            source: 'ai',
            locked_by_user: false,
          });
          return;
        }
        if (typeof item === 'object') {
          const safeItem = item as Record<string, unknown>;
          const parsedId = Number(safeItem.id);
          const parsedSort = Number(safeItem.sort_order);
          expanded.push({
            id: Number.isFinite(parsedId) ? parsedId : categoryIndex * 1000 + itemIndex + 1,
            category,
            title: toSafeString(safeItem.title),
            is_checked: Boolean(safeItem.is_checked),
            sort_order: Number.isFinite(parsedSort) ? parsedSort : itemIndex,
            source: toSafeString(safeItem.source) || 'ai',
            locked_by_user: Boolean(safeItem.locked_by_user),
          });
        }
      });
    });

    return expanded.filter(item => item.title.trim().length > 0);
  }

  return [];
};

const normalizeCategory = (value: string) => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : 'Other';
};

const TripPackingList: FC<TripPackingListProps> = ({ tripId, stopId, variant = 'default' }) => {
  const embedded = variant === 'embedded';
  const isMobile = useIsMobile();
  const isStopLevel = typeof stopId === 'number';
  const emptyStateMessage = isStopLevel
    ? 'No packing list generated yet for this stop. Click “Generate Packing List for this Stop” to get suggested items.'
    : tripEmptyStateLineTwo;

  const [newItemByCategory, setNewItemByCategory] = useState<Record<string, string>>({});
  const [requestError, setRequestError] = useState('');
  const [itemPendingDelete, setItemPendingDelete] = useState<PackingListItem | null>(null);
  const [isDeletingItem, setIsDeletingItem] = useState(false);

  const {
    data: tripPackingList,
    isLoading: isTripLoading,
    refetch: refetchTripPackingList,
  } = useGetTripPackingListQuery(tripId, { skip: isStopLevel });

  const {
    data: stopPackingList,
    isLoading: isStopLoading,
    refetch: refetchStopPackingList,
  } = useGetStopPackingListQuery(stopId as number, { skip: !isStopLevel });

  const [generateTripPackingList, { isLoading: isGeneratingTrip }] = useGenerateTripPackingListMutation();
  const [generateStopPackingList, { isLoading: isGeneratingStop }] = useGenerateStopPackingListMutation();
  const [createTripPackingListItem] = useCreateTripPackingListItemMutation();
  const [createStopPackingListItem] = useCreateStopPackingListItemMutation();
  const [updateTripPackingListItem] = useUpdateTripPackingListItemMutation();
  const [updateStopPackingListItem] = useUpdateStopPackingListItemMutation();
  const [deleteTripPackingListItem] = useDeleteTripPackingListItemMutation();
  const [deleteStopPackingListItem] = useDeleteStopPackingListItemMutation();

  const activePackingList = isStopLevel ? stopPackingList : tripPackingList;
  const parsedItems = useMemo(() => normalizeItems(activePackingList?.items), [activePackingList?.items]);
  const isLoading = isStopLevel ? isStopLoading : isTripLoading;
  const isGenerating = isStopLevel ? isGeneratingStop : isGeneratingTrip;

  const groupedItems = useMemo(() => {
    const map = new Map<string, PackingListItem[]>();
    parsedItems.forEach(item => {
      const category = normalizeCategory(item.category);
      const list = map.get(category) ?? [];
      list.push({ ...item, category });
      map.set(category, list);
    });

    const sortedEntries = Array.from(map.entries()).map(([category, items]) => {
      const sortedItems = [...items].sort((a, b) => {
        if (a.is_checked !== b.is_checked) return Number(a.is_checked) - Number(b.is_checked);
        if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
        return a.title.localeCompare(b.title);
      });
      return [category, sortedItems] as const;
    });

    sortedEntries.sort((a, b) => a[0].localeCompare(b[0]));
    return sortedEntries;
  }, [parsedItems]);

  const categoriesToRender = groupedItems.length > 0 ? groupedItems.map(([category]) => category) : FALLBACK_CATEGORIES;

  const getItemSortOrderForToggle = (item: PackingListItem, nextChecked: boolean) => {
    const categoryItems = parsedItems.filter(existing => normalizeCategory(existing.category) === normalizeCategory(item.category));
    if (nextChecked) {
      const maxSort = categoryItems.reduce((max, current) => Math.max(max, current.sort_order), 0);
      return maxSort + 1;
    }

    return 0;
  };

  const handleGenerate = async () => {
    setRequestError('');
    try {
      const hasExistingList = parsedItems.length > 0;
      const regeneratePayload = hasExistingList ? { mode: 'replace_all' as const } : undefined;
      if (isStopLevel) {
        await generateStopPackingList({
          stopId: stopId as number,
          ...(regeneratePayload ? { payload: regeneratePayload } : {}),
        }).unwrap();
        await refetchStopPackingList();
      } else {
        await generateTripPackingList({
          tripId,
          ...(regeneratePayload ? { payload: regeneratePayload } : {}),
        }).unwrap();
        await refetchTripPackingList();
      }
    } catch {
      setRequestError('Failed to generate packing list. Please try again.');
    }
  };

  const handleToggle = async (item: PackingListItem) => {
    setRequestError('');
    try {
      const nextChecked = !item.is_checked;
      const payload = {
        is_checked: nextChecked,
        sort_order: getItemSortOrderForToggle(item, nextChecked),
        locked_by_user: true,
      };
      if (isStopLevel) {
        await updateStopPackingListItem({ stopId: stopId as number, itemId: item.id, payload }).unwrap();
      } else {
        await updateTripPackingListItem({ tripId, itemId: item.id, payload }).unwrap();
      }
    } catch {
      setRequestError('Failed to update item state.');
    }
  };

  const handleConfirmRemove = async () => {
    if (!itemPendingDelete) return;
    setRequestError('');
    setIsDeletingItem(true);
    try {
      if (isStopLevel) {
        await deleteStopPackingListItem({ stopId: stopId as number, itemId: itemPendingDelete.id }).unwrap();
      } else {
        await deleteTripPackingListItem({ tripId, itemId: itemPendingDelete.id }).unwrap();
      }
      setItemPendingDelete(null);
    } catch {
      setRequestError('Failed to delete item.');
    } finally {
      setIsDeletingItem(false);
    }
  };

  const renderDeleteModal = () => (
    <Modal
      open={Boolean(itemPendingDelete)}
      onClose={() => {
        if (!isDeletingItem) setItemPendingDelete(null);
      }}
      aria-labelledby="packing-delete-modal-title"
      aria-describedby="packing-delete-modal-description"
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(34, 43, 58, 0.2)',
        },
      }}
    >
      <Box className={styles.deleteModal}>
        <Box className={styles.deleteModalContent}>
          <Typography
            id="packing-delete-modal-title"
            variant="h5"
            sx={{ fontWeight: 'bold', textAlign: 'center', color: '#222B3A' }}
          >
            Are you sure?
          </Typography>
          <Typography
            id="packing-delete-modal-description"
            variant="body1"
            sx={{ color: '#222B3A', textAlign: 'center' }}
          >
            Do you really want to delete this item?
          </Typography>

          <Box className={styles.deleteModalActions}>
            <CustomButton
              variant="outlined"
              onClick={() => setItemPendingDelete(null)}
              disabled={isDeletingItem}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="contained"
              onClick={handleConfirmRemove}
              disabled={isDeletingItem}
              customClass={styles.deleteConfirmButton}
            >
              {isDeletingItem ? 'Deleting...' : 'Delete'}
            </CustomButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );

  const handleSaveNewItem = async (category: string) => {
    const rawTitle = newItemByCategory[category] || '';
    const nextTitle = rawTitle.trim();
    if (!nextTitle.length) return;
    const payload = {
      category,
      title: nextTitle,
    };
    setRequestError('');
    try {
      if (isStopLevel) {
        await createStopPackingListItem({ stopId: stopId as number, payload }).unwrap();
      } else {
        await createTripPackingListItem({ tripId, payload }).unwrap();
      }
      setNewItemByCategory(prev => {
        const next = { ...prev };
        delete next[category];
        return next;
      });
    } catch {
      setRequestError('Failed to add new item.');
    }
  };

  const renderCategoryBlocks = () => (
    <Box className={styles.categoriesContainer}>
      {categoriesToRender.map(category => {
        const categoryItems = groupedItems.find(([name]) => name === category)?.[1] ?? [];
        const showEditorRow = Object.prototype.hasOwnProperty.call(newItemByCategory, category);
        return (
          <Box
            key={category}
            className={styles.categoryBlock}
          >
            <Typography
              variant="h10Semibold"
              className={styles.categoryTitle}
            >
              {category}
            </Typography>
            {categoryItems.map(item => {
              return (
                <Box
                  key={`${normalizeCategory(category)}-${item.id}`}
                  className={styles.itemRow}
                >
                  <Checkbox
                    checked={item.is_checked}
                    onChange={e => {
                      e.stopPropagation();
                      handleToggle(item);
                    }}
                    className={styles.checkbox}
                    icon={<Box className={styles.checkboxIcon} />}
                    checkedIcon={
                      <Box className={styles.checkboxIconChecked}>
                        <CheckIcon className={styles.checkboxCheckmark} />
                      </Box>
                    }
                  />
                  <Typography
                    variant="h11Semibold"
                    className={`${styles.itemTitle} ${item.is_checked ? styles.checked : ''}`}
                  >
                    {item.title}
                  </Typography>
                  <IconButton
                    size="small"
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      setItemPendingDelete(item);
                    }}
                    className={styles.deleteButton}
                    aria-label="Delete item"
                  >
                    <DeleteIcon size={24} />
                  </IconButton>
                </Box>
              );
            })}
            {showEditorRow ? (
              <Box className={styles.addRow}>
                <CustomInput
                  value={newItemByCategory[category] ?? ''}
                  onChange={e => setNewItemByCategory(prev => ({ ...prev, [category]: e.target.value }))}
                  inputLabel="New Item"
                  customClass={styles.addInput}
                  placeholder=" "
                />
                <Box className={styles.addRowActions}>
                  <CustomButton
                    variant="outlined"
                    customClass={styles.saveButton}
                    onClick={() => handleSaveNewItem(category)}
                    disabled={!(newItemByCategory[category] || '').trim().length}
                  >
                    Save
                  </CustomButton>
                  <CustomButton
                    variant="outlined"
                    customClass={styles.cancelButton}
                    onClick={() =>
                      setNewItemByCategory(prev => {
                        const next = { ...prev };
                        delete next[category];
                        return next;
                      })
                    }
                  >
                    Cancel
                  </CustomButton>
                </Box>
              </Box>
            ) : (
               <CustomButton
               variant="outlined"
               onClick={() => setNewItemByCategory(prev => ({ ...prev, [category]: '' }))}
               customClass={styles.addButton}
               startIcon={
                 <AddIcon
                   sx={{
                     fontSize: 24,
                     width: 24,
                     height: 24,
                   }}
                 />
               }
             >
               <span style={{ fontWeight: '700', fontSize: '14px' }}>Add New</span>
             </CustomButton>
            )}
          </Box>
        );
      })}
    </Box>
  );

  if (embedded) {
    return (
      <>
      <Box
        className={styles.contentWrapper}
        data-embedded
      >
        <Box className={styles.embeddedButtonRow}>
          <CustomButton
            variant="contained"
            onClick={handleGenerate}
            customClass={styles.aiButton}
            startIcon={<AiIcon />}
            disabled={isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Generate Packing List'}
          </CustomButton>
        </Box>
        {isLoading ? (
          <Typography
            variant="h10"
            className={styles.loadingText}
          >
            Loading packing list...
          </Typography>
        ) : parsedItems.length === 0 ? (
          <Typography
            variant="h10"
            className={styles.emptyStateSingle}
            component="p"
            sx={{ fontWeight: '600', letterSpacing: '-1px' }}
          >
            {emptyStateMessage}
          </Typography>
        ) : (
          renderCategoryBlocks()
        )}
        {requestError && (
          <Typography
            variant="h11"
            className={styles.errorText}
          >
            {requestError}
          </Typography>
        )}
      </Box>
      {renderDeleteModal()}
      </>
    );
  }

  return (
    <>
    <Box className={styles.contentWrapper}>
      {!isMobile && (
        <Box className={styles.header}>
          <Typography
            variant="h7Bold"
            className={styles.title}
          >
            Packing List
          </Typography>
          <CustomButton
            variant="contained"
            onClick={handleGenerate}
            customClass={styles.aiButton}
            startIcon={<AiIcon />}
            disabled={isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Generate Packing List'}
          </CustomButton>
        </Box>
      )}

      {isMobile && (isLoading || parsedItems.length > 0) && (
        <Box className={styles.mobileGenerateRow}>
          <CustomButton
            variant="contained"
            onClick={handleGenerate}
            customClass={styles.aiButton}
            startIcon={<AiIcon />}
            disabled={isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Generate Packing List'}
          </CustomButton>
        </Box>
      )}

      {isLoading ? (
        <Typography
          variant="h10"
          className={styles.loadingText}
        >
          Loading packing list...
        </Typography>
      ) : parsedItems.length === 0 ? (
        <Box className={styles.noPackingList}>
          <Typography variant="h10" sx={{ fontWeight: '600' }}>{tripEmptyStateLineOne}</Typography>
          <Typography variant="h10" sx={{ fontWeight: '600' }}>{emptyStateMessage}</Typography>
        </Box>
      ) : (
        renderCategoryBlocks()
      )}
      {requestError && (
        <Typography
          variant="h11"
          className={styles.errorText}
        >
          {requestError}
        </Typography>
      )}

      {isMobile && !isLoading && parsedItems.length === 0 && (
        <Box className={styles.mobileGenerateRow}>
          <CustomButton
            variant="contained"
            onClick={handleGenerate}
            customClass={styles.aiButton}
            startIcon={<AiIcon />}
            disabled={isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Generate Packing List'}
          </CustomButton>
        </Box>
      )}
    </Box>
    {renderDeleteModal()}
    </>
  );
};

export default TripPackingList;
